import { cva, type VariantProps } from 'class-variance-authority';

import type { CodeInputActiveRange } from './CodeInput.types';

/**
 * One-time-code cells — Figma "UX/UI WebApp" `pKO9bydKQ3f8mJgggb6Idv`, frame
 * 30000:39591 ("Verify"). Six 40×40 cells JOINED into one 240px group: each
 * cell owns its top, bottom and right border, the first one also its left, so
 * neighbours share a single 1px line and the group measures exactly
 * `length × 40` — no gaps to add up wrong.
 *
 * The active cell (the one the next digit lands in, while the field is
 * focused) carries the SAME border the DS `Input` uses for its "Active
 * typing" state — `border-secondary-800`, read from `Input.variants.ts` and
 * not re-chosen here. That dark edge IS the caret: the Figma frame draws no
 * caret bar, and a hand-rolled blinking rule would be a second, unmanaged
 * animation token.
 *
 * Every cell after the first has no left border of its own: its left edge is
 * the previous cell's right border. So the active cell only reads as closed
 * when that neighbour's right border takes the active colour too
 * (`beforeActive`). Recolouring the shared line keeps the strip's geometry
 * fixed; overlapping borders (`-ml-px`) would shift the cells as the caret
 * moves.
 *
 * Radius is the outer one only — 14px on the group's left and right ends
 * (`rounded-l-*` on the first cell, `rounded-r-*` on the last), so the strip
 * reads as one field rather than six chips.
 *
 * Dark mode is automatic: `secondary-*` resolves through the auto-inverting
 * CSS-var palette, exactly as the Input's borders do. No `dark:` classes, no
 * hex.
 */

/** DS points, at scale 1 — the Figma cell is a 40px square. */
export const CODE_INPUT_CELL_SIZE = 40;

/**
 * The joined strip. `self-center` rather than a margin on the caller: the
 * code step centres the group and nothing else on that row, so centring
 * belongs to the group.
 */
export const codeInputGroupClass = 'flex flex-row items-center self-center';

/**
 * One cell. `position` collapses the shared borders and paints the two outer
 * corners; `active` recolours the cell to the Input's typing border;
 * `beforeActive` recolours the right border this cell shares with the active
 * one, which is the active cell's left edge.
 *
 * NO z-index, in any state. No two cells' borders overlap, so there is
 * nothing to lift, and on native a z-index is harmful: the cells' row is
 * layout-only, so Fabric flattens it and the cells become siblings of the
 * native fork's invisible TextInput. Fabric orders siblings by z-index, so
 * a cell raised on focus reorders them, and the differ moves the TextInput
 * out of the window and back. iOS drops focus from a field that leaves the
 * window, so the keyboard came up and went straight back down.
 */
export const codeInputCellVariants = cva(
  'flex h-10 w-10 items-center justify-center border-y border-r border-secondary-300',
  {
    variants: {
      position: {
        first: 'rounded-l-[14px] border-l',
        middle: '',
        last: 'rounded-r-[14px]',
        only: 'rounded-[14px] border-l',
      },
      /** Figma DS Input "Active typing" — see the note above. */
      active: { true: 'border-secondary-800', false: '' },
      /** The cell just left of the active one — see the note above. */
      beforeActive: { true: 'border-r-secondary-800', false: '' },
    },
    defaultVariants: { position: 'middle', active: false, beforeActive: false },
  },
);

/**
 * The digit itself — B1 (16/24) medium in `secondary-950`, the same weight
 * ramp as a filled `Input` value one step darker, because a code cell holds
 * one glyph and reads lighter than a sentence at the same token.
 *
 * Native gets a fontSize-only class for the reason `inputTextVariants`
 * documents: an explicit lineHeight makes a single-line RN Text sit low in
 * its box, and the fixed 40px cell already owns the vertical rhythm.
 */
export const codeInputCellTextClass =
  'font-medium text-secondary-950 native:font-mona-medium web:text-b1 native:text-[1rem]';

export type CodeInputCellVariantProps = VariantProps<typeof codeInputCellVariants>;

/**
 * The web fork's active cells, from `input-otp`'s per-slot `isActive`: the
 * caret slot alone, or every slot in the selection. `null` when none is.
 */
export function activeRangeFromSlots(
  slotIsActive: readonly boolean[],
): CodeInputActiveRange | null {
  const start = slotIsActive.indexOf(true);
  if (start === -1) return null;
  return { start, end: slotIsActive.lastIndexOf(true) + 1 };
}

/**
 * Which `position` variant the cell at `index` of `length` takes. Exported so
 * both platform forks — and the spec — read the same rule instead of each
 * spelling out the first/last arithmetic.
 */
export function codeInputCellPosition(
  index: number,
  length: number,
): NonNullable<CodeInputCellVariantProps['position']> {
  if (length === 1) return 'only';
  if (index === 0) return 'first';
  if (index === length - 1) return 'last';
  return 'middle';
}
